import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, GitMerge, ShieldCheck, AlertTriangle, Phone, Mail } from 'lucide-react';
import { TierBadge } from '../TierBadge';
import { toast } from 'sonner';

const tierLabel = { elite: 'Elite', soulai_paid: 'SoulAI Paid', trial_paid: 'Trial', free: 'Free', gold: 'Elite' };

export function MergeDuplicatesDialog({ open, onClose, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [confirmId, setConfirmId] = useState(null);   // survivor_id of the group awaiting confirm
  const [mergingId, setMergingId] = useState(null);

  const fetchGroups = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/users/duplicates')
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => toast.error('Could not load duplicates'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) { fetchGroups(); setConfirmId(null); } }, [open, fetchGroups]);

  const doMerge = async (g) => {
    setMergingId(g.survivor_id);
    try {
      const { data } = await api.post('/api/admin/users/merge', {
        survivor_id: g.survivor_id,
        loser_ids: g.loser_ids,
      });
      toast.success(`Merged ${data.merged} account${data.merged > 1 ? 's' : ''} → ${data.survivor_email} (${tierLabel[data.resulting_tier] || data.resulting_tier})`);
      setGroups((prev) => prev.filter((x) => x.survivor_id !== g.survivor_id));
      setConfirmId(null);
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMergingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merge-duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" /> Merge duplicate leads
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review and merge learner accounts that share a phone number or email.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-2">
          Accounts that share a phone number or email. The <strong>top-tier</strong> account is kept and
          receives <strong>all</strong> batches, courses &amp; entitlements from the others; duplicates are
          removed. This can't be undone.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Scanning for duplicates…
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="merge-no-duplicates">
            <ShieldCheck className="h-10 w-10 text-emerald-500 mb-3" />
            <p className="font-medium">No duplicate leads found</p>
            <p className="text-sm text-muted-foreground">Every learner has a unique phone & email.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.survivor_id} className="rounded-xl border p-4" data-testid={`merge-group-${g.survivor_id}`}>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {g.match_reason === 'phone'
                      ? <><Phone className="h-4 w-4" /> Same number <span className="font-mono text-foreground">{g.shared_phone}</span></>
                      : <><Mail className="h-4 w-4" /> Same email</>}
                    <span className="mx-1">·</span>
                    <span>{g.members.length} accounts → keep <span className="font-semibold text-foreground">{tierLabel[g.resulting_tier] || 'Free'}</span></span>
                  </div>
                  {confirmId === g.survivor_id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Sure?</span>
                      <Button size="sm" variant="destructive" onClick={() => doMerge(g)} disabled={mergingId === g.survivor_id}
                        data-testid={`merge-confirm-${g.survivor_id}`}>
                        {mergingId === g.survivor_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm merge'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setConfirmId(g.survivor_id)} data-testid={`merge-start-${g.survivor_id}`}>
                      <GitMerge className="h-4 w-4 mr-1" /> Merge {g.members.length}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {g.members.map((m) => {
                    const isSurvivor = m.id === g.survivor_id;
                    return (
                      <div key={m.id}
                        className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${isSurvivor ? 'bg-primary/5 border border-primary/20' : 'bg-muted/40'}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{m.name || '(no name)'}</span>
                            <TierBadge tier={m.learner_type} />
                            {m.learner_type === 'free' && <span className="text-[10px] text-muted-foreground">Free</span>}
                            {m.is_suspended && <Badge variant="destructive" className="text-[10px]">Suspended</Badge>}
                            {!m.is_active && !m.is_suspended && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{m.email} · {m.phone_normalized || m.phone || 'no phone'}</div>
                        </div>
                        {isSurvivor
                          ? <Badge className="shrink-0 bg-primary/10 text-primary border-primary/20">Keeps this account</Badge>
                          : <span className="text-xs text-muted-foreground shrink-0">merges in →</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
